import CancelIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Box, Typography } from '@mui/material'
import { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react'
import HintIcon from '../../../../assets/icons/hint.svg?react'
import { nameOfSquare, pieceSymbol } from '../../../../chess/basics'
import { Color, GameTree } from '../../../../chess/types'
import useCorrectPieceColor from '../../../../hooks/useCorrectPieceColor'
import { Square } from '../../../../react-chessboard/src/chessboard/types'
import { PiecesTheme, piecesProvider } from '../../../../sharedComponents/src/globalHeader/common/PiecesProvider'
import { colors } from '../../../../sharedComponents/src/globalHeader/theme/colors'

export type NotationProps = {
    notations: NotationsType[]
    setNotations: Dispatch<SetStateAction<NotationsType[]>>
    currentHint: Square | undefined
    gameTree: GameTree
    usedShowSolution: boolean
    puzzleState: boolean | null
    currentPositionId: string
    setCurrentPositionId: (nextPositionId: string) => void
    myColor: Color
    disabled: boolean
}

export type NotationsType = {
    location: string
    id: string
    piece: string
    playersMove: boolean
    usedHint: boolean
    usedShowSolution: boolean
    isLost: boolean
    isCorrect: boolean
    nextPositionId: string
}

type NotationItemProps = {
    notation: NotationsType
}

const NotationItem: FC<NotationItemProps> = ({ notation }) => {
    const getIconAndLabel = () => {
        if (notation.isLost) return { icon: <CancelIcon />, label: 'Incorrect' }
        if (notation.usedHint) return { icon: <HintIcon />, label: 'Hint' }
        if (notation.isCorrect) return { icon: <CheckCircleIcon />, label: 'Correct' }
        return null
    }

    const iconAndLabel = getIconAndLabel()
    const locationColor = notation.usedShowSolution || notation.usedHint ? colors.glow.main : 'inherit'

    return (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Box component="span" sx={{ color: locationColor }}>
                {notation.location}
            </Box>
            {iconAndLabel && (
                <>
                    <Box component="span">:</Box>
                    {iconAndLabel.icon}
                    <Box component="span">{iconAndLabel.label}</Box>
                </>
            )}
        </Typography>
    )
}

type MoveNumberProps = {
    number: number
}

const MoveNumber: FC<MoveNumberProps> = ({ number }) =>
    number > 1 && (
        <Typography lineHeight="48px" fontSize="16px" variant="body2">
            {number - 1}.
        </Typography>
    )

type MoveItemProps = {
    move: NotationsType
    isActive: boolean
    disabled: boolean
    onClick: () => void
}

const MoveItem: FC<MoveItemProps> = ({ move, isActive, disabled, onClick }) => {
    const pieces = piecesProvider(PiecesTheme.DEFAULT)
    const pieceColor = move.usedShowSolution ? colors.glow.main : move.piece[0] === 'b' ? 'black' : ''
    const pieceStroke = move.piece[0] === 'b' && !move.usedShowSolution ? 'white' : ''

    const getIconColor = () => {
        if (move.isLost) return colors.error.dark
        if (move.isCorrect && !move.usedHint) return colors.success.dark
        return ''
    }

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '48px',
                padding: '4px',
                borderRadius: '0.25rem',
                cursor: disabled ? 'default' : 'pointer',
                backgroundColor: isActive ? 'background.default' : 'transparent',
                border: `1px solid ${isActive ? 'white' : 'transparent'} !important`,
                ':hover': {
                    backgroundColor: disabled ? 'transparent' : 'background.default',
                },
            }}
        >
            <Box width="32px" height="32px" sx={useCorrectPieceColor(pieceColor, pieceStroke)}>
                {pieces[move.piece]?.({ isDragging: false, squareWidth: '100%' })}
            </Box>
            <Box
                sx={{
                    userSelect: 'none',
                    svg: {
                        width: '30px',
                        height: '30px',
                        margin: '0 0.25rem',
                        '> path': {
                            fill: getIconColor(),
                        },
                    },
                }}
            >
                <NotationItem notation={move} />
            </Box>
        </Box>
    )
}

export const Notations: FC<NotationProps> = ({
    currentHint,
    gameTree,
    usedShowSolution,
    puzzleState,
    setCurrentPositionId,
    currentPositionId,
    notations,
    setNotations,
    myColor,
    disabled,
}) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const currentPositionIsLast = currentPositionId === notations[notations.length - 1]?.nextPositionId

    const scrollToTop = () => {
        const container = containerRef.current
        if (container) {
            container.scrollTo({ top: 0, behavior: 'auto' })
        }
    }

    useEffect(() => {
        scrollToTop()
    }, [notations])

    useEffect(() => {
        // Check if puzzle changed by comparing move IDs
        const puzzleChanged =
            notations.length > 0 && gameTree.moves.length > 0 && notations[0]?.id !== gameTree.moves[0]?.id

        if (puzzleChanged || (gameTree.moves.length === 0 && notations.length > 0)) {
            // Reset notations when puzzle changes
            setNotations([])
        } else if (gameTree.moves.length > notations.length) {
            const newItemIndex = notations.length
            const move = gameTree.moves[newItemIndex]
            const position = gameTree.positions[newItemIndex]
            const piece = pieceSymbol(position.position, move.move.from)
            const playersMove = position.position.turn === myColor

            setNotations((prevNotations) =>
                prevNotations.concat({
                    location: nameOfSquare(move.move.to),
                    id: move.id,
                    piece,
                    playersMove,
                    usedHint: nameOfSquare(move.move.from) === currentHint,
                    usedShowSolution: playersMove && usedShowSolution,
                    isLost: puzzleState === false,
                    isCorrect: playersMove && puzzleState !== false,
                    nextPositionId: move.nextPositionId,
                }),
            )
        } else if (gameTree.moves.length < notations.length) {
            setNotations((prevNotations) => prevNotations.slice(0, -1))
        }
    }, [gameTree.moves.length, currentHint, usedShowSolution, puzzleState, gameTree.moves])

    const handlePositionChange = (move: NotationsType) => {
        if (!disabled) {
            setCurrentPositionId(move.nextPositionId)
        }
    }

    // Group moves: show dots at start, then pair bot move + player move
    const movePairs: Array<{ white?: NotationsType; black?: NotationsType; moveNumber: number; showDots?: boolean }> =
        []

    let moveCounter = 1

    // Always show dots at the start as first row (even if no notations)
    const firstBotMove = notations.find((m) => !m.playersMove)
    if (firstBotMove) {
        movePairs.push({ black: firstBotMove, moveNumber: moveCounter, showDots: true })
    } else if (notations.length === 0) {
        // Show dots as fallback when no notations
        movePairs.push({ moveNumber: moveCounter, showDots: true })
    }

    notations.forEach((move) => {
        // Skip first bot move since it's already added with dots
        if (!move.playersMove && movePairs.length === 1 && movePairs[0]?.black === move) {
            return
        }

        if (move.playersMove) {
            // Player move on left - start new row
            moveCounter++
            movePairs.push({ white: move, moveNumber: moveCounter })
        } else {
            // Bot move on right - add to last row
            const lastPair = movePairs[movePairs.length - 1]
            if (lastPair && !lastPair.black) {
                lastPair.black = move
            }
        }
    })

    return (
        <Box
            ref={containerRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                overflow: 'auto',
                px: { xs: '8px', md: '15%' },
                flexGrow: 1,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column-reverse', gap: '4px' }}>
                {movePairs.map((pair, pairIndex) => {
                    const isActive = (move: NotationsType) =>
                        move.nextPositionId === currentPositionId && !currentPositionIsLast

                    return (
                        <Box key={pairIndex} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <MoveNumber number={pair.moveNumber} />

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexGrow: 1,
                                }}
                            >
                                {pair.white ? (
                                    <MoveItem
                                        move={pair.white}
                                        isActive={isActive(pair.white)}
                                        disabled={disabled}
                                        onClick={() => handlePositionChange(pair.white!)}
                                    />
                                ) : pair.showDots ? (
                                    <Typography lineHeight="48px" fontSize="16px" fontWeight="bold" variant="body2">
                                        ...
                                    </Typography>
                                ) : (
                                    <Box />
                                )}

                                {pair.black && (
                                    <MoveItem
                                        move={pair.black}
                                        isActive={isActive(pair.black)}
                                        disabled={disabled}
                                        onClick={() => handlePositionChange(pair.black!)}
                                    />
                                )}
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}
